import { ArrowCircleLeftIcon } from "@phosphor-icons/react/dist/csr/ArrowCircleLeft";
import { CheckCircleIcon } from "@phosphor-icons/react/dist/csr/CheckCircle";
import { CircleIcon } from "@phosphor-icons/react/dist/csr/Circle";
import { ClockIcon } from "@phosphor-icons/react/dist/csr/Clock";
import { WarningCircleIcon } from "@phosphor-icons/react/dist/csr/WarningCircle";
import { XCircleIcon } from "@phosphor-icons/react/dist/csr/XCircle";
import { type ReactNode } from "react";
import { Alert, AlertDescription } from "@volca/ui/components/ui/alert";
import { Badge, type BadgeColor } from "@volca/ui/components/ui/badge";
import { Button } from "@volca/ui/components/ui/button";
import { SimpleSidebarHeader } from "@volca/ui/components/ui/simple-sidebar";
import { Skeleton } from "@volca/ui/components/ui/skeleton";
import { Stack } from "@volca/ui/components/ui/stack";
import { Text } from "@volca/ui/components/ui/text";
import { cn } from "@volca/ui/lib/utils";
import type { ApAgentAction } from "@/accounts-payable/types";
import {
  CONFIDENCE_TEXT_CLASS,
  confidenceLabel,
  confidenceTone,
  pricebookCostChange,
} from "@/accounts-payable/utils";

export type StatusBadgeMeta = {
  label: string;
  quiet?: boolean;
  color: BadgeColor;
};

/**
 * Bright status pill — reserved for state (Applied, Rejected, Matched, …),
 * driven by one of the maps in utils.ts.
 */
export const StatusBadge = ({
  meta,
  size = "sm",
  quietWhenSettled = false,
}: {
  meta: StatusBadgeMeta;
  size?: "sm" | "md" | "lg";
  quietWhenSettled?: boolean;
}) =>
  quietWhenSettled && meta.quiet ? (
    <span className="text-sm text-subtle">{meta.label}</span>
  ) : (
    <Badge variant="secondary" color={meta.color} size={size}>
      {meta.label}
    </Badge>
  );

export const ActionStatusGlyph = ({
  action,
  className = "h-4 w-4 flex-none",
  outline = false,
}: {
  action: ApAgentAction;
  className?: string;
  outline?: boolean;
}) => {
  const weight = outline ? "regular" : "fill";
  const named = (label: string) =>
    ({ role: "img", "aria-label": label, weight }) as const;

  if (action.rollback_status === "rolled_back")
    return (
      <ArrowCircleLeftIcon
        {...named("Reversed")}
        className={cn(className, "text-subtle")}
      />
    );
  // A rollback failure is often a legitimate "can't reverse, data moved on" and
  // is Admin-only territory, so it reads as muted info, not a red error.
  if (action.rollback_status === "rollback_failed")
    return (
      <WarningCircleIcon
        {...named("Couldn't be reversed")}
        className={cn(className, "text-subtle")}
      />
    );
  if (action.status === "rejected")
    return (
      <XCircleIcon
        {...named("Rejected")}
        className={cn(className, "text-danger")}
      />
    );
  // Retired by a newer run rather than by a person's decision, so the same
  // mark as rejected without the red.
  if (action.status === "superseded")
    return (
      <XCircleIcon
        {...named("Superseded")}
        className={cn(className, "text-subtle")}
      />
    );
  // An apply failure means the change never landed — the one failure the user
  // should notice, so it stays red.
  if (action.status === "apply_failed")
    return (
      <WarningCircleIcon
        {...named("Couldn't be applied")}
        className={cn(className, "text-danger")}
      />
    );
  if (action.status === "approved" || action.status === "applying")
    return (
      <ClockIcon
        {...named("Applying")}
        className={cn(className, "text-information")}
      />
    );
  // Not yet decided: an open checkbox, so a pending row reads as a to-do in
  // the same list as the resolved ones.
  if (action.status === "pending_review")
    return (
      <CircleIcon
        role="img"
        aria-label="Awaiting review"
        className={cn(className, "text-subtle")}
      />
    );
  // rolling_back/rollback_blocked intentionally fall through to the applied
  // glyph — transients that settle on their own, so no separate mark.
  return (
    <CheckCircleIcon
      {...named("Applied")}
      className={cn(className, "text-success")}
    />
  );
};

/**
 * Footer "Load more" for the paginated lists. Render it only while another
 * page exists, and disable it while any fetch is in flight — fetchNextPage
 * would cancel an in-flight refetch and leave stale rows on screen.
 */
export const LoadMoreButton = ({
  onClick,
  loading,
  disabled,
}: {
  onClick: () => void;
  loading: boolean;
  disabled?: boolean;
}) => (
  <Stack
    align="start"
    gap={4}
    direction="row"
    justify="center"
    className="w-full flex-none"
  >
    <Button
      variant="secondary"
      size="sm"
      onClick={onClick}
      disabled={disabled || loading}
      loading={loading}
      loadingLabel="Load more, in progress"
    >
      Load more
    </Button>
  </Stack>
);

/**
 * Loading / error body for the detail sidebars, which fetch their record by
 * id — the panel is already open while that fetch is in flight, so it needs
 */
export const DetailSidebarFallback = ({
  title,
  loading,
  errorMessage,
  onClose,
}: {
  title: string;
  loading: boolean;
  errorMessage: string;
  onClose: () => void;
}) => (
  <Stack align="start" gap={0} className="h-full w-full">
    <SimpleSidebarHeader title={title} onClose={onClose} />
    <Stack align="start" gap={4} className="w-full px-5 py-6">
      {loading ? (
        <>
          <Skeleton className="h-5 w-24 rounded-full" />
          <Skeleton className="h-6 w-56" />
          <Skeleton className="h-40 w-full rounded-lg" />
        </>
      ) : (
        <span className="text-sm text-subtle">{errorMessage}</span>
      )}
    </Stack>
  </Stack>
);

/**
 * "87% confidence" in its traffic-light colour: green ≥90%, amber 70–90%, red
 * below. Semibold at every tone — at this size colour alone lets the green and
 * amber recede — and the % is always spelled out, so colour is never the only
 * signal.
 */
export const ConfidenceLabel = ({
  value,
  className,
}: {
  value: number;
  className?: string;
}) => (
  <Text
    weight="semibold"
    variant="caption"
    className={cn(CONFIDENCE_TEXT_CLASS[confidenceTone(value)], className)}
  >
    {confidenceLabel(value)}
  </Text>
);

/**
 * Heading for a top-level section of a detail panel (Document, Activity…).
 * Same weight as the section headings in the Spiffs sidebars.
 */
export const SectionHeading = ({ children }: { children: ReactNode }) => (
  <Text variant="title">{children}</Text>
);

/**
 * Quiet label for a group inside a section (Reason, Line items, Comments).
 * Matches the label style of the core `InfoWithLabel`.
 */
export const SectionLabel = ({ children }: { children: ReactNode }) => (
  <Text variant="label" tone="muted">
    {children}
  </Text>
);

/**
 * Shown while an agent run exists but hasn't produced proposals yet — chiefly
 * the window right after a manual job match, which enqueues a run server-side.
 * The surrounding query polls, so this resolves into the action list on its own.
 */
export const AgentRunningNotice = () => (
  <Alert role="status" className="px-3 py-2">
    <ClockIcon className="animate-pulse text-subtle" />
    <AlertDescription>
      The agent is running against this invoice. Proposals appear here when it
      finishes.
    </AlertDescription>
  </Alert>
);

/**
 * Signed percent change of a pricebook cost, colored by direction. Renders
 * nothing when there is no current cost to compare against.
 */
export const CostChangePct = ({
  current,
  proposed,
  suffix,
  className,
}: {
  current: number;
  proposed: number;
  /** Trailing label, e.g. "vs current". */
  suffix?: string;
  className?: string;
}) => {
  const { direction, pct } = pricebookCostChange(current, proposed);
  if (pct === null) return null;

  const sign = pct > 0 ? "+" : ""; // negative values already have a sign
  return (
    <span
      className={cn(
        // green-70 (6.8:1 on white), the step --text-interactive now uses;
        // green-60 passed AA too (4.8:1), this keeps the two greens in step.
        "font-semibold",
        {
          "text-danger": direction === "raise",
          "text-success": direction === "lower",
        },
        className,
      )}
    >
      {`${sign}${pct.toFixed(1)}%${suffix ? ` ${suffix}` : ""}`}
    </span>
  );
};
